import {
  Button,
  cn,
  Divider,
  Pill,
  RiDownloadLine,
  RiExternalLinkLine,
  RiImageLine,
  RiRefreshLine,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'component-library';
import i18next from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { OrganizationReport } from '@/api/rest/resources/types/organization';
import { REPORT_TYPES } from '@/api/rest/resources/types/reports';
import { Body, Header } from '@/components/widgets/a/Text/Text';
import { useGenerateReportByType } from '@/pages/shared/hooks/useReports';
import { isNullish } from '@/utils/booleans';

export type Report = OrganizationReport & { to?: string };

type ReportCardProps = {
  report: Report;
  onDownload?: (report: Report) => void;
  onClick?: (report: Report) => void;
  className?: string;
};

export const ReportCard = ({ report, onDownload, onClick, className }: ReportCardProps) => {
  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload?.(report);
  };
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(report);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18next.language, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <Stack
      data-testid='report-card'
      className={cn(
        'rounded-3xl border border-neutral-black-12 p-6 transition-all duration-200 gap-2 justify-between',
        'w-[380px] h-[320px]',
        className,
      )}
    >
      <ReportThumbnail report={report} />
      <ReportInfo report={report} formatDate={formatDate} />
      <Divider />
      <Stack direction='row' className='justify-end gap-2'>
        {!isNullish(report.to) && <LinkAction onClick={handleClick} />}
        {report.report_type && <ReGenerateReportAction reportType={report.report_type} />}
        {report.file_download_url !== '' && <DownloadAction onDownload={handleDownload} />}
      </Stack>
    </Stack>
  );
};

const ReportThumbnail = ({ report }: { report: Report }) => {
  return (
    <Stack data-testid='report-card-thumbnail' className='h-32'>
      {report.thumbnail_url ? (
        <img
          src={report.thumbnail_url}
          alt={`Thumbnail for ${report.report_name}`}
          className='max-h-full max-w-full object-contain rounded-lg'
        />
      ) : (
        <Stack className='text-neutral-black-40 text-center'>
          <RiImageLine className='w-16 h-16 mx-auto mb-2' />
          <Body i18nPath='reports.no_thumbnail' className='text-neutral-black-40' />
        </Stack>
      )}
    </Stack>
  );
};

const ReportInfo = ({ report, formatDate }: { report: Report; formatDate: (date: string) => string }) => {
  return (
    <Stack data-testid='report-card-info' spacing={2}>
      <Stack direction='row' className='justify-between items-start gap-2'>
        <Header text={report.report_name} className='line-clamp-2 text-neutral-black-80' />
        <ReportFilterBadge isFiltered={report.is_filtered} />
      </Stack>
      <Stack spacing={1}>
        <Body text={formatDate(report.report_date)} className='text-neutral-black-60' />
      </Stack>
    </Stack>
  );
};

const ReportFilterBadge = ({ isFiltered }: { isFiltered: boolean }) => {
  const { t } = useTranslation();

  if (!isFiltered) {
    return null;
  }

  return (
    <Pill data-testid='report-filter-badge' size='small' className='shrink-0 bg-neutral-black-10 text-neutral-black-60'>
      {t('reports.badge.filtered')}
    </Pill>
  );
};

const LinkAction = ({ onClick }: { onClick: (e: React.MouseEvent) => void }) => {
  return (
    <Button onClick={onClick} variant='text' size='small' aria-label='Open report'>
      <RiExternalLinkLine className='text-primary-100' />
    </Button>
  );
};

const DownloadAction = ({ onDownload }: { onDownload: (e: React.MouseEvent) => void }) => {
  return (
    <Button onClick={onDownload} variant='text' size='small' aria-label='Download report'>
      <RiDownloadLine className='text-primary-100' />
    </Button>
  );
};

const ReGenerateReportAction = ({ reportType }: { reportType: (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES] }) => {
  const { t } = useTranslation();

  const { mutate: generateReport, isPending } = useGenerateReportByType({
    reportType,
  });

  const handleClick = () => {
    generateReport();
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild className='items-center'>
        <Button onClick={handleClick} variant='text' size='small' aria-label='Re-generate report' loading={isPending}>
          <RiRefreshLine className='text-primary-100' />
        </Button>
      </TooltipTrigger>
      <TooltipContent className='whitespace-pre-wrap'>{t('reports.reGenerateReport')}</TooltipContent>
    </Tooltip>
  );
};
